/*
 * pogi-sizing-modal — PDP buy-box "sizing aid" modals (vanilla, no deps).
 * ---------------------------------------------------------------------------
 * Two tools, one shell:
 *   kind="pads"        → Pad Finder      (one Q: dog size → recommends Size)
 *   kind="bags"        → Bag Calculator  (two Qs: # dogs + pickup → Count)
 *   kind="compostable" → Bag Calculator  (same; no scent axis)
 * Wipes gets NONE (deliberate product decision).
 *
 * Markup is rendered by snippets/pogi-sizing-modal.liquid INSIDE each buy-box
 * block, so the modal can reach its sibling <pogi-buy-box-*> element.
 *
 * WRITE-BACK = native variant selection only. We .click() the buy box's own
 * option buttons ([data-pogi-tile] for pad Size, [data-pogi-size] for bag
 * Count within the CURRENT format), whose delegated handlers drive Horizon's
 * hidden <variant-picker> → price/featured-media/cart all stay native. We
 * never change Format or Scent, never toggle Subscribe, never add to cart.
 *
 * ⚠ HEURISTICS — none of these are sourced data. A vet/ops gut-check is the
 * only thing between them and being wrong in public. Confirm before/after launch.
 */
(() => {
  'use strict';

  /* i18n: JA overrides come from the [data-sm-i18n] blob the snippet renders via
     `| t`. EVERY read falls back to the exact English literal that used to be
     hardcoded, so a missing key can never change the EN site. [[name]] placeholder
     (not {{ }}) so Liquid's t-filter never eats it. The blob is parsed once per
     modal in the constructor; the reader is the per-instance this.t(). */
  const smTpl = (I, k, fb, vars) => {
    let s = (I && I[k] != null) ? I[k] : fb;
    if (vars) for (const key of Object.keys(vars)) s = s.split('[[' + key + ']]').join(String(vars[key]));
    return s;
  };

  /* ---- ⚠ HEURISTIC CONSTANTS — researched June 2026, still warrant a
     vet/ops gut-check before relying on them in public. Source per value. -- */

  // Pads "~N a month" context line: assumed pad changes/day (month = changes*30).
  // Research: puppies in active training change 3–4×/day (vets advise "at least
  // 3× a day"); maintenance/senior use ~1–2×/day. 2 = conservative-typical central
  // estimate across that mix. ⚠ training-heavy buyers run higher.
  //   Porch Potty: https://porchpotty.com/blogs/news/how-often-to-change-puppy-training-pads
  //   Bulldogology: https://www.bulldogology.net/how-often-should-i-change-my-dogs-pee-pad/
  const PAD_CHANGES_PER_DAY = 2;

  // Dog-size band → recommended live Size option value (exact label match).
  // Bands CONFIRMED by independent pee-pad sizing guides: Medium 18×24 for small
  // dogs & puppies (<25 lb), Large 24×24 for most adults (25–60 lb), XL 24×35 for
  // big/senior dogs (60+ lb). Universal advice: "when in doubt, size up."
  //   Scott Brand: https://www.scottbrand.com/en-us/tips-and-advice/pet-care/finding-right-dog-pad
  //   Bark Potty: https://barkpotty.com/blogs/bark-blog/bark-potty-pad-size-guide-matching-pad-dimensions-to-your-dog-s-breed
  //   Four Paws: https://www.fourpaws.com/pets-101/potty-time-and-training/selecting-wee-wee-pad-for-your-dog
  // rank = the size's position in the buy box's size axis (Medium=0, Large=1, XL=2).
  // Write-back selects the rank-th [data-pogi-tile] size tile, NOT a localized string,
  // so "Use this size" works on EN and /ja alike (Shopify translates the option-value
  // text but preserves option-value ORDER across locales). sizeValue kept only as an
  // EN-store fallback / for the result name.
  // Dims are shown in the LOCALE'S unit: EN keeps INCHES (w/h, US convention) byte-
  // /ja shows CENTIMETRES rounded to NICE numbers (owner rule, not raw ceil):
  // M 45×60, L 60×60, XL 60×90. The `dims` t() gets all four; each locale's pogi.sm.dims picks its pair.
  const PAD_MAP = {
    small:  { rank: 0, sizeValue: 'Medium (18 x 24 in)',      name: 'Medium',      w: 18, h: 24, wc: 45, hc: 60 },
    medium: { rank: 1, sizeValue: 'Large (24 x 24 in)',       name: 'Large',       w: 24, h: 24, wc: 60, hc: 60 },
    large:  { rank: 2, sizeValue: 'Extra Large (24 x 35 in)', name: 'Extra Large', w: 24, h: 35, wc: 60, hc: 90 },
  };
  // Glyph footprint uses cm so its proportions match the /ja dims; EN ratios identical
  // (cm is a linear scaling of in), so the footprint looks the same on both stores.
  const PAD_MAX_DIM = 90; // shared yardstick (cm) so XL reads biggest in the glyph

  // Bags: avg poops/day per dog (size-independent — you bag each pickup). Vet
  // consensus is 1–3×/day, most commonly 1–2 for healthy adults → 2.0 grounded
  // central estimate (prototype used 2.5, which is high). ⚠ puppies/high-fiber
  // diets run higher; the 2-month pick buffer below absorbs the variance.
  //   Dogster: https://www.dogster.com/dog-health-care/how-many-times-a-day-should-a-dog-poop
  //   PetMD: https://www.petmd.com/dog/general-health/how-often-should-dogs-poop
  //   AKC: https://www.akc.org/expert-advice/health/how-often-should-my-dog-be-pooping/
  const BAG_POOPS_PER_DAY = 2.0;
  // Pickup factor — ⚠ INTERNAL ESTIMATE, no external source: in a yard you scoop
  // several piles into one bag, so fewer bags than 1-per-pickup on walks.
  const BAG_FACTOR = { walks: 1, yard: 0.6, both: 1 };
  // ⚠ MERCHANDISING CHOICE, not biology: pick the smallest count covering ~this
  // many days so reorders aren't constant but stock isn't excessive.
  const BAG_SUPPLY_DAYS = 60; // ≈ 2 months

  /* ---- inline icons (self-hosted; no unpkg dependency) ----------------- */
  // Lucide "dog" icon (self-hosted, no unpkg dependency).
  const SVG_DOG = (px) => `<svg width="${px}" height="${px}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11.25 16.25h1.5L12 17z"/><path d="M16 14v.5"/><path d="M4.42 11.247A13.152 13.152 0 0 0 4 14.556C4 18.728 7.582 21 12 21s8-2.272 8-6.444a11.702 11.702 0 0 0-.493-3.309"/><path d="M8 14v.5"/><path d="M8.5 8.5c-.384 1.05-1.083 2.028-2.344 2.5-1.931.722-3.576-.297-3.656-1-.113-.994 1.177-6.53 4-7 1.923-.321 3.651.845 3.651 2.235A7.497 7.497 0 0 1 14 5.277c0-1.39 1.844-2.598 3.767-2.277 2.823.47 4.113 6.006 4 7-.08.703-1.725 1.722-3.656 1-1.261-.472-1.855-1.45-2.239-2.5"/></svg>`;
  const SVG_RULER = `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m21.3 8.7-8.6 8.6a1 1 0 0 1-1.4 0l-2.6-2.6a1 1 0 0 1 0-1.4l8.6-8.6a1 1 0 0 1 1.4 0l2.6 2.6a1 1 0 0 1 0 1.4Z"/><path d="m7.5 10.5 2 2"/><path d="m10.5 7.5 2 2"/><path d="m13.5 4.5 2 2"/><path d="m4.5 13.5 2 2"/></svg>`;

  const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  const bagsIn = (label) => { const m = String(label).match(/(\d[\d,]*)\s*bags/i); return m ? parseInt(m[1].replace(/,/g, ''), 10) : 0; };

  /* ---- the controller, one per modal ----------------------------------- */
  class SizingModal {
    constructor(dialog) {
      this.dialog = dialog;
      this.kind = dialog.dataset.kind; // pads | bags | compostable
      // i18n blob (JA overrides) rendered by the snippet; EN fallbacks stay inline.
      const i18nEl = dialog.querySelector('[data-sm-i18n]');
      this.I = {};
      if (i18nEl) { try { this.I = JSON.parse(i18nEl.textContent); } catch (e) { /* keep EN fallbacks */ } }
      this.t = (k, fb, vars) => smTpl(this.I, k, fb, vars);
      // bags use [data-pogi-size] count pills; compostable uses [data-pogi-count] cards.
      this.countSel = this.kind === 'compostable' ? '[data-pogi-count]' : '[data-pogi-size]';
      this.buyBox = dialog.closest('pogi-buy-box-pads, pogi-buy-box-bags, pogi-buy-box-compostable')
        || (dialog.closest('.shopify-section') || document).querySelector('pogi-buy-box-pads, pogi-buy-box-bags, pogi-buy-box-compostable');
      this.body = dialog.querySelector('[data-sm-body]');
      this.cta = dialog.querySelector('[data-sm-cta]');
      this.help = dialog.querySelector('[data-sm-help]');
      this.rec = null;

      // open / close
      const root = this.buyBox || (dialog.closest('.shopify-section') || document);
      root.querySelectorAll('[data-pogi-sizing-open]').forEach((b) => {
        b.addEventListener('click', () => this.open());
      });
      dialog.querySelector('[data-sm-close]')?.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); }); // backdrop
      // <dialog> handles Esc natively.
      this.cta?.addEventListener('click', () => { this.apply(); dialog.close(); });

      this.render();           // build the question UI once
      this.state = this.kind === 'pads' ? { size: 'medium' } : { dogs: 1, pickup: 'both' };
      this.compute();          // live result from defaults
    }

    open() {
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal(); else this.dialog.setAttribute('open', '');
      // Recompute against the buy box's CURRENT format/state on every open, so the
      // result + helper line always match what's selected (and what write-back applies)
      // even if the shopper changed format before opening.
      this.compute();
    }

    /* ---- build the inputs (once) --------------------------------------- */
    render() {
      if (this.kind === 'pads') {
        const qDogSize = this.t('q_dog_size', 'Dog size');
        this.body.innerHTML = `
          <div class="pogi-sm__qlabel">${esc(qDogSize)}</div>
          <div class="pogi-sm__seg pogi-sm__seg--3" role="radiogroup" aria-label="${esc(qDogSize)}" data-sm-seg="size">
            ${[['small', this.t('dog_small', 'Small'), this.t('dog_small_sub', 'under 25 lb'), 17], ['medium', this.t('dog_medium', 'Medium'), this.t('dog_medium_sub', '25–60 lb'), 22], ['large', this.t('dog_large', 'Large'), this.t('dog_large_sub', '60+ lb'), 28]]
              .map(([v, l, sub, ic]) => `
              <button type="button" role="radio" class="pogi-sm__tile" data-val="${v}" aria-checked="false">
                <span class="pogi-sm__tile-ic">${SVG_DOG(ic)}</span>
                <span class="pogi-sm__tile-l">${l}</span>
                <span class="pogi-sm__tile-sub">${sub}</span>
              </button>`).join('')}
          </div>
          <div class="pogi-sm__result" data-sm-result></div>`;
        this.body.querySelector('[data-sm-seg="size"]').addEventListener('click', (e) => {
          const b = e.target.closest('[data-val]'); if (!b) return;
          this.state.size = b.dataset.val; this.compute();
        });
      } else {
        const qWhere = this.t('q_where', 'Where you pick up');
        this.body.innerHTML = `
          <div class="pogi-sm__row">
            <span class="pogi-sm__qlabel">${esc(this.t('q_num_dogs', 'Number of dogs'))}</span>
            <span class="pogi-sm__stepper">
              <button type="button" data-sm-step="-1" aria-label="${esc(this.t('fewer_dogs', 'fewer dogs'))}">&minus;</button>
              <output data-sm-dogs>1</output>
              <button type="button" data-sm-step="1" aria-label="${esc(this.t('more_dogs', 'more dogs'))}">+</button>
            </span>
            <span class="pogi-sm__pack" data-sm-pack aria-hidden="true"></span>
          </div>
          <div class="pogi-sm__qlabel pogi-sm__qlabel--mt">${esc(qWhere)}</div>
          <div class="pogi-sm__seg pogi-sm__seg--3" role="radiogroup" aria-label="${esc(qWhere)}" data-sm-seg="pickup">
            ${[['walks', this.t('pickup_walks', 'On walks')], ['yard', this.t('pickup_yard', 'In the yard')], ['both', this.t('pickup_both', 'Both')]]
              .map(([v, l]) => `<button type="button" role="radio" class="pogi-sm__pillopt" data-val="${v}" aria-checked="false">${l}</button>`).join('')}
          </div>
          <div class="pogi-sm__result" data-sm-result></div>`;
        this.body.querySelector('[data-sm-seg="pickup"]').addEventListener('click', (e) => {
          const b = e.target.closest('[data-val]'); if (!b) return;
          this.state.pickup = b.dataset.val; this.compute();
        });
        this.body.querySelectorAll('[data-sm-step]').forEach((b) => b.addEventListener('click', () => {
          const d = parseInt(b.dataset.smStep, 10);
          this.state.dogs = Math.min(9, Math.max(1, this.state.dogs + d));
          this.compute();
        }));
      }
    }

    /* ---- live compute + result ----------------------------------------- */
    compute() {
      // reflect selected inputs
      this.body.querySelectorAll('[data-sm-seg] [data-val]').forEach((b) => {
        const seg = b.closest('[data-sm-seg]').dataset.smSeg;
        b.setAttribute('aria-checked', String(b.dataset.val === this.state[seg]));
      });
      const out = this.body.querySelector('[data-sm-result]');
      if (this.kind === 'pads') {
        const p = PAD_MAP[this.state.size];
        const month = PAD_CHANGES_PER_DAY * 30;
        this.rec = { rank: p.rank, sizeValue: p.sizeValue };
        const scale = 84 / PAD_MAX_DIM; // PAD_MAX_DIM is cm → use the cm footprint
        // dims t() carries BOTH unit pairs; the locale's pogi.sm.dims picks one
        // (EN → "[[w]] × [[h]] in", JA → "[[wc]] × [[hc]] cm"). EN stays byte-identical.
        out.innerHTML = `
          <span class="pogi-sm__eyebrow">${esc(this.t('best_fit', 'Best fit'))}</span>
          <div class="pogi-sm__big">${esc(p.name)}</div>
          <span class="pogi-sm__dims">${SVG_RULER}${this.t('dims', '[[w]] × [[h]] in', { w: p.w, h: p.h, wc: p.wc, hc: p.hc })}</span>
          <div class="pogi-sm__glyphtile">
            <span class="pogi-sm__glyphbox"><span class="pogi-sm__glyph" style="width:${(p.wc * scale).toFixed(0)}px;height:${(p.hc * scale).toFixed(0)}px"></span></span>
            <span class="pogi-sm__glyphcap">${esc(this.t('actual_proportions', 'actual proportions'))}</span>
          </div>
          <p class="pogi-sm__ctx">${this.t('pads_ctx_html', "You'll go through about <b>[[month]] pads a month</b>, based on roughly [[changes]] changes a day.", { month: month, changes: PAD_CHANGES_PER_DAY })}</p>`;
        this.cta.textContent = this.t('pads_cta', 'Select this size · [[name]]', { name: p.name });
        this.help.textContent = this.t('pads_help', "We'll set your size in the buy box. Add Subscribe & Save there for 10% off if you like.");
      } else {
        const perDay = this.state.dogs * BAG_POOPS_PER_DAY * BAG_FACTOR[this.state.pickup];
        const year = Math.round(perDay * 365);
        const week = Math.round(perDay * 7);
        this.rec = { perDay, year, week };
        // growing dog pack
        const pack = this.body.querySelector('[data-sm-pack]');
        if (pack) pack.innerHTML = Array.from({ length: this.state.dogs }, () => `<span class="pogi-sm__packdog">${SVG_DOG(24)}</span>`).join('');
        const dogsOut = this.body.querySelector('[data-sm-dogs]'); if (dogsOut) dogsOut.textContent = this.state.dogs;
        // [[dogs]] = a complete noun phrase. English needs plural ("1 dog" / "2 dogs"),
        // which can't be resolved through the static blob, so JS builds the phrase from
        // the dog_one / dog_other count strings (EN fallbacks here). JA has no plural, so
        // both forms carry the same "[[n]]頭" — one number, correct either way.
        const dogsLabel = this.t(this.state.dogs === 1 ? 'dog_one' : 'dog_other',
          this.state.dogs + (this.state.dogs === 1 ? ' dog' : ' dogs'), { n: this.state.dogs });
        out.innerHTML = `
          <span class="pogi-sm__eyebrow">${esc(this.t('your_estimate', 'Your estimate'))}</span>
          <div class="pogi-sm__big pogi-sm__big--num">${year.toLocaleString()}</div>
          <span class="pogi-sm__unit">${esc(this.t('bags_unit', 'poop bags a year'))}</span>
          <p class="pogi-sm__ctx">${this.t('bags_ctx_html', 'About <b>[[week]] a week</b> across [[dogs]].', { week: week, dogs: dogsLabel })}</p>`;
        this.cta.textContent = this.t('bags_cta', 'Use suggested bags');
        this.help.textContent = this.bagHelper();
      }
    }

    /* ---- bags: read CURRENT format + its rendered counts, pick one ------ */
    currentCounts() {
      // The buy box re-renders count buttons per format, so the visible ones
      // ARE the current format's counts. (bags=[data-pogi-size], comp=[data-pogi-count])
      if (!this.buyBox) return [];
      return [...this.buyBox.querySelectorAll(this.countSel)]
        .filter((b) => b.offsetParent !== null || b.style.display !== 'none')
        .map((b) => { const value = b.dataset.pogiSize || b.dataset.pogiCount; return { value, bags: bagsIn(value), el: b }; })
        .filter((c) => c.value && c.bags > 0);
    }
    currentFormatName() {
      if (!this.buyBox) return '';
      const f = this.buyBox.format; // bags: string; compostable: object {name}
      if (f && typeof f === 'object' && f.name) return f.name;
      if (typeof f === 'string' && f) return f;
      const lab = this.buyBox.querySelector('[data-pogi-format-label]');
      if (lab && lab.textContent.trim()) return lab.textContent.trim();
      const on = this.buyBox.querySelector('[data-pogi-format][aria-checked="true"]');
      return on ? on.textContent.trim() : '';
    }
    pickCount() {
      const counts = this.currentCounts();
      if (!counts.length || !this.rec) return null;
      const need = this.rec.perDay * BAG_SUPPLY_DAYS;
      const sorted = counts.slice().sort((a, b) => a.bags - b.bags);
      return sorted.find((c) => c.bags >= need) || sorted[sorted.length - 1];
    }
    bagHelper() {
      const yr = (this.rec.year || 0).toLocaleString();
      const fmt = this.currentFormatName();
      const pick = this.pickCount();
      if (!pick || !fmt) return this.t('bags_help_generic', "That's about [[yr]] bags a year. We'll set a sensible count in the buy box. Add Subscribe & Save for 10% off if you like.", { yr: yr });
      return this.t('bags_help_pick', "That's about [[yr]] bags a year. We'll set the [[count]] of [[fmt]] in the buy box. Add Subscribe & Save for 10% off if you like.", { yr: yr, count: pick.value, fmt: fmt });
    }

    /* ---- write-back: native variant selection via the buy box's buttons - */
    apply() {
      if (!this.buyBox || !this.rec) return;
      if (this.kind === 'pads') {
        // Locale-proof: the buy box renders the size tiles in the size axis's own
        // order (Medium, Large, XL → rank 0,1,2), one [data-pogi-tile] per size in
        // that order, on EN and /ja alike (Shopify translates the labels but keeps
        // option-value order). Click the rank-th tile. Fall back to the exact EN
        // option-value string (works on the EN store if order ever shifts).
        const tiles = [...this.buyBox.querySelectorAll('[data-pogi-tile]')];
        let btn = (this.rec.rank != null && this.rec.rank < tiles.length) ? tiles[this.rec.rank] : null;
        if (!btn) btn = this.buyBox.querySelector(`[data-pogi-tile="${cssEsc(this.rec.sizeValue)}"]`);
        if (btn) btn.click();
      } else {
        const pick = this.pickCount();
        if (!pick) return;
        // pick.el is the live button; click it (count only, current format kept).
        if (pick.el && pick.el.isConnected) pick.el.click();
        else { const b = this.buyBox.querySelector(this.countSel.replace(']', '="' + cssEsc(pick.value) + '"]')); if (b) b.click(); }
      }
    }
  }

  // CSS.escape fallback for attribute-selector values (parens/spaces in labels).
  const cssEsc = (s) => (window.CSS && CSS.escape) ? CSS.escape(s) : String(s).replace(/["\\]/g, '\\$&');

  const init = () => document.querySelectorAll('[data-pogi-sizing-modal]:not([data-sm-ready])').forEach((d) => {
    d.setAttribute('data-sm-ready', '1');
    try { new SizingModal(d); } catch (e) { /* never block the PDP */ }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
